import React from 'react';
import { DollarSign, FileText, TrendingUp, Users, RefreshCw, Download } from 'lucide-react';
import { GiftRecord, GiftStatistics, GiftType } from '../../../types/invitation';
import ThemedCard from '../../themed/ThemedCard';
import { formatAmount, formatGiftType } from '../types';

interface OverviewTabProps {
  giftStats: GiftStatistics;
  giftRecords: GiftRecord[];
  loading: boolean;
  exporting: boolean;
  onRefresh: () => Promise<void>;
  onExport: () => Promise<void>;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  giftStats,
  giftRecords,
  loading,
  exporting,
  onRefresh,
  onExport,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center">
        <div className="flex space-x-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-wedding-pink/10 text-wedding-pink rounded-lg hover:bg-wedding-pink/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            重新整理
          </button>
          <button
            onClick={onExport}
            disabled={exporting || giftRecords.length === 0}
            className="flex items-center px-4 py-2 bg-wedding-pink text-white rounded-lg hover:bg-wedding-pink/90 transition-colors disabled:opacity-50"
          >
            <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-spin' : ''}`} />
            匯出 CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ThemedCard className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <DollarSign className="h-5 w-5 text-wedding-pink mr-2" />
                <p className="text-sm text-wedding-text-body">總收入</p>
              </div>
              <p className="text-2xl font-bold text-wedding-text-title">
                {formatAmount(giftStats.totalAmount)}
              </p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <FileText className="h-5 w-5 text-wedding-pink mr-2" />
                <p className="text-sm text-wedding-text-body">記錄數量</p>
              </div>
              <p className="text-2xl font-bold text-wedding-text-title">
                {giftStats.totalRecords}
              </p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <TrendingUp className="h-5 w-5 text-wedding-pink mr-2" />
                <p className="text-sm text-wedding-text-body">平均金額</p>
              </div>
              <p className="text-2xl font-bold text-wedding-text-title">
                {formatAmount(giftStats.averageAmount)}
              </p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <Users className="h-5 w-5 text-wedding-pink mr-2" />
                <p className="text-sm text-wedding-text-body">親友金額</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-wedding-text-body">男方:</span>
                  <span className="text-lg font-semibold text-wedding-text-title">
                    {formatAmount(giftStats.guestSideStats?.groom?.totalAmount || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-wedding-text-body">女方:</span>
                  <span className="text-lg font-semibold text-wedding-text-title">
                    {formatAmount(giftStats.guestSideStats?.bride?.totalAmount || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ThemedCard>
      </div>

      {Object.keys(giftStats.giftTypeBreakdown).length > 0 && (
        <ThemedCard className="p-6">
          <h3 className="text-lg font-semibold text-wedding-text-title mb-4">紅包類型分佈</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(giftStats.giftTypeBreakdown).map(([type, data]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-wedding-pink/5 rounded-lg">
                <span className="text-wedding-text-body font-medium">{formatGiftType(type as GiftType)}</span>
                <div className="text-right">
                  <div className="text-sm text-wedding-text-body">{data.count} 筆</div>
                  <div className="font-semibold text-wedding-text-title">
                    {formatAmount(data.total)}
                  </div>
                  <div className="text-sm text-wedding-pink">({data.percentage}%)</div>
                </div>
              </div>
            ))}
          </div>
        </ThemedCard>
      )}
    </div>
  );
};
